import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Alert,
  IconButton,
  Button
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  MenuBook as MenuBookIcon,
  CalendarMonth as CalendarMonthIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import MfaManagementTab from '../components/Settings/MfaManagementTab';
import ThemeBackground from '../components/Layout/ThemeBackground';

function UserSettings() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  if (!user) {
    return (
      <ThemeBackground variant="full">
        <div className="container mx-auto px-2 sm:px-4 py-4 sm:py-6">
          <div className="max-w-7xl mx-auto">
            <Alert 
              severity="error"
              className="glass-card"
              sx={{
                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.2)',
                color: '#fca5a5'
              }}
            >
              Please log in to access settings
            </Alert>
          </div>
        </div>
      </ThemeBackground>
    );
  }

  return (
    <ThemeBackground variant="full">
      <div className="container mx-auto px-2 sm:px-4 py-4 sm:py-6">
        <div className="max-w-7xl mx-auto">
          {/* Header Section - canonical admin look */}
          {user?.role === 'administrator' && (
            <div className="glass-card-light mb-6">
              <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
                <div className="flex justify-between items-start flex-wrap gap-4">
                  <div className="flex flex-col space-y-4">
                    <div className="flex space-x-4">
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          fontWeight: 700,
                          minWidth: '160px',
                          backgroundColor: '#2196f3',
                          '&:hover': { backgroundColor: '#1976d2' }
                        }}
                        onClick={() => navigate('/')}
                      >
                        Back to Home
                      </Button>
                    </div>
                  </div>
                  <div className="text-right">
                    <Typography
                      variant="h3"
                      component="h1"
                      gutterBottom
                      className="theme-text-primary"
                      sx={{ fontFamily: 'Roboto', fontWeight: 800, fontSize: '2.5rem', letterSpacing: '0.5px' }}
                    >
                      Security Settings
                    </Typography>
                    <Typography
                      variant="subtitle1"
                      className="theme-text-secondary"
                      sx={{ fontWeight: 500, fontSize: '1.1rem' }}
                    >
                      Manage your password and multi-factor authentication
                    </Typography>
                  </div>
                </div>
              </div>
            </div>
          )}
          {user?.role !== 'administrator' && (
          <div className="glass-card p-4 sm:p-6 mb-4 sm:mb-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                  <IconButton
                    onClick={() => navigate(
                      user?.role === 'counsellor' ? '/counsellor/dashboard'
                      : user?.role === 'assessor' ? '/assessor'
                      : '/'
                    )}
                    sx={{
                      color: 'rgba(255, 255, 255, 0.8)',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        color: '#667eea'
                      }
                    }}
                  >
                    <ArrowBackIcon />
                  </IconButton>
                  <Typography 
                    variant="h4" 
                    component="h1" 
                    className="theme-text-primary"
                    sx={{
                      fontWeight: 700,
                      fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' }
                    }}
                  >
                    Security Settings
                  </Typography>
                </Box>
                <Typography 
                  variant="subtitle1"
                  className="theme-text-secondary"
                  sx={{
                    fontWeight: 400,
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    ml: 7
                  }}
                >
                  Manage your password and multi-factor authentication
                </Typography>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 w-full sm:w-auto">
                <Button
                  variant="contained"
                  onClick={() => navigate('/counsellor/appointments')}
                  startIcon={<CalendarMonthIcon />}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 3,
                    py: 1,
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    background: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)',
                    boxShadow: '0 4px 15px rgba(33, 150, 243, 0.4)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                      boxShadow: '0 6px 20px rgba(33, 150, 243, 0.6)'
                    }
                  }}
                >
                  Appointments
                </Button>
                <Button
                  variant="contained"
                  onClick={() => navigate('/counsellor/policies')}
                  startIcon={<MenuBookIcon />}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 3,
                    py: 1,
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                    boxShadow: '0 4px 15px rgba(245, 158, 11, 0.4)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #d97706 0%, #b45309 100%)',
                      boxShadow: '0 6px 20px rgba(245, 158, 11, 0.6)'
                    }
                  }}
                >
                  Policies & Procedures
                </Button>
                <Button
                  variant="outlined"
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 3,
                    py: 1,
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                    color: 'rgba(255, 255, 255, 0.9)',
                    '&:hover': {
                      borderColor: 'rgba(255, 255, 255, 0.5)',
                      backgroundColor: 'rgba(255, 255, 255, 0.1)'
                    }
                  }}
                  onClick={handleLogout}
                >
                  Logout
                </Button>
              </div>
            </div>
          </div>
          )}

          {/* Alerts */}
          {error && (
            <Alert 
              severity="error" 
              onClose={() => setError(null)}
              className="glass-card"
              sx={{ 
                mb: 3,
                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.2)',
                color: '#fca5a5'
              }}
            >
              {error}
            </Alert>
          )}

          {success && (
            <Alert 
              severity="success" 
              onClose={() => setSuccess(null)}
              className="glass-card"
              sx={{ 
                mb: 3,
                backgroundColor: 'rgba(16, 185, 129, 0.1)',
                border: '1px solid rgba(16, 185, 129, 0.2)',
                color: '#6ee7b7'
              }}
            >
              {success}
            </Alert>
          )}

          {/* Security Settings Card with Glass Morphism */}
          <Box className="glass-card" sx={{ width: '100%', p: 3 }}>
            <MfaManagementTab 
              user={user}
              onError={setError}
              onSuccess={setSuccess}
            />
          </Box>
        </div>
      </div>
    </ThemeBackground>
  );
}

export default UserSettings;
